
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { 
  ReferralFriend, 
  ReferralStats, 
  ReferralReward, 
  UseReferralsReturn 
} from "./types";
import { createReferralLink, getReferralCodeFromCookie } from "./referralUtils";
import { useReferralUrlParams } from "./useReferralUrlParams";
import { useCopyReferralLink } from "./useCopyReferralLink";
import { useClaimReward } from "./useClaimReward";
import { fetchReferralData, fetchReferralRewards } from "./referralApi";

export const useReferrals = (): UseReferralsReturn => {
  const { toast } = useToast();
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referredFriends, setReferredFriends] = useState<ReferralFriend[]>([]);
  const [rewards, setRewards] = useState<ReferralReward[]>([]);
  const [referralStats, setReferralStats] = useState<ReferralStats>({
    totalReferrals: 0,
    paidReferrals: 0,
    nextMilestone: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  
  // Check URL for referral params
  useReferralUrlParams();
  
  // Hook for copying referral link
  const { copyReferralLink: copyLink } = useCopyReferralLink();
  
  // Hook for claiming rewards
  const { claimReward } = useClaimReward(rewards, setRewards);
  
  // Create referral link
  const referralLink = createReferralLink(referralCode);
  
  // Copy referral link wrapper that matches the expected return type
  const copyReferralLink = async (): Promise<void> => {
    if (referralLink) {
      await copyLink(referralLink);
    }
  };

  // Load referral data
  useEffect(() => {
    const loadReferralData = async () => {
      setIsLoading(true);
      
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setIsLoading(false);
          return;
        }
        
        // Get referral code from user metadata or generate one
        const userData = await fetchReferralData(user.id);
        
        if (userData) {
          setReferralCode(userData.referralCode);
          setReferredFriends(userData.referredFriends);
          setReferralStats({
            totalReferrals: userData.totalReferrals,
            paidReferrals: userData.paidReferrals,
            nextMilestone: userData.nextMilestone
          });
          
          // Fetch rewards
          const rewardsData = await fetchReferralRewards(user.id);
          setRewards(rewardsData || []);
        }
      } catch (error) {
        console.error("Error loading referral data:", error);
        toast({
          title: "Error",
          description: "Could not load referral data",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadReferralData();
  }, [toast]);

  return {
    referralCode,
    referralLink,
    referredFriends,
    referralStats,
    rewards,
    isLoading,
    getReferralCodeFromCookie,
    copyReferralLink,
    claimReward
  };
};
